import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { useUpdatePushTemplateMutation } from '../../redux/features/notificationTemplate/notificationTemplate.apiSlice';
import {
  NotificationTemplateCopy,
  PushNotificationTemplate,
  PushTemplateSnapshot,
  UpdatePushTemplateRequest,
} from '../../types/notificationTemplate';
import { NotificationTemplateHistory } from './NotificationTemplateHistory';
import { CopyFormValues, TemplateCopyFields } from './TemplateCopyFields';
import {
  describeKeyRename,
  describeProblem,
  describeSaveError,
  isValidKey,
  KEY_MAX_LENGTH,
  newPlaceholders,
  validateTemplateCopy,
} from './templateCopy';

type FormValues = CopyFormValues & { key: string; isActive: boolean };

const toFormValues = (template: PushNotificationTemplate | null): FormValues => ({
  key: template?.key || '',
  titleAr: template?.titleAr || '',
  titleEn: template?.titleEn || '',
  bodyAr: template?.bodyAr || '',
  bodyEn: template?.bodyEn || '',
  isActive: template?.isActive ?? true,
});

/** What the server stores for these values: trimmed text, and a blank title as null. */
const toSnapshot = (values: FormValues): Required<Omit<PushTemplateSnapshot, 'category'>> => ({
  key: values.key.trim(),
  titleAr: values.titleAr.trim() || null,
  titleEn: values.titleEn.trim() || null,
  bodyAr: values.bodyAr.trim(),
  bodyEn: values.bodyEn.trim(),
  isActive: values.isActive,
});

const copyOf = ({ titleAr, titleEn, bodyAr, bodyEn }: NotificationTemplateCopy): NotificationTemplateCopy => ({
  titleAr,
  titleEn,
  bodyAr,
  bodyEn,
});

interface NotificationTemplateDialogProps {
  template: PushNotificationTemplate | null;
  /** Every push template, so a new key that another one has is caught before the round trip. */
  templates: PushNotificationTemplate[];
  canUpdate: boolean;
  onClose: () => void;
}

/**
 * Edit one push template's key and copy, and switch it on or off. When it is sent is decided by the code that sends
 * its key.
 */
export function NotificationTemplateDialog({
  template,
  templates,
  canUpdate,
  onClose,
}: NotificationTemplateDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [updateTemplate, { isLoading }] = useUpdatePushTemplateMutation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const { values, handleChange, setFieldValue, submitForm } = useFormik<FormValues>({
    initialValues: toFormValues(template),
    enableReinitialize: true,
    onSubmit: async (formValues) => {
      if (!template) {
        return;
      }

      const saved = toSnapshot(toFormValues(template));
      const next = toSnapshot(formValues);
      const changes = (Object.keys(next) as (keyof typeof next)[]).reduce(
        (acc, field) => (next[field] === saved[field] ? acc : { ...acc, [field]: next[field] }),
        {} as Partial<UpdatePushTemplateRequest>,
      );

      setSaveErrors([]);

      try {
        await updateTemplate({ id: template.id, ...changes }).unwrap();
        enqueueSnackbar(`Template saved. It applies to the next notification sent with ${next.key}.`, {
          variant: 'success',
        });
        onClose();
      } catch (err) {
        setSaveErrors(describeSaveError(err, 'The template could not be saved.'));
      }
    },
  });

  const saved = useMemo(() => toSnapshot(toFormValues(template)), [template]);
  const edited = toSnapshot(values);
  const copyChanged = (['titleAr', 'titleEn', 'bodyAr', 'bodyEn'] as const).some(
    (field) => edited[field] !== saved[field],
  );
  const keyChanged = edited.key !== saved.key;
  const keyError =
    keyChanged && !isValidKey(edited.key) ? describeProblem({ code: 'INVALID_KEY', key: edited.key }) : '';
  const taken = keyChanged && templates.some((other) => other.id !== template?.id && other.key === edited.key);
  const isDirty = keyChanged || copyChanged || edited.isActive !== saved.isActive;
  // The server checks the copy on every edit but a plain switch-off; mirror that, so a template with broken copy can
  // still be switched off.
  const problems =
    isDirty && (keyChanged || copyChanged || edited.isActive)
      ? validateTemplateCopy(copyOf(edited)).map(describeProblem)
      : [];
  const introduced = copyChanged ? newPlaceholders(copyOf(saved), copyOf(edited)) : [];

  const handleClose = () => {
    setSaveErrors([]);
    onClose();
  };

  return (
    <Dialog open={Boolean(template)} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <span>Push template</span>
          <Typography component="code" sx={{ fontFamily: 'monospace' }}>
            {template?.key}
          </Typography>
          {template?.category && <Chip size="small" label={template.category} />}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 1 }}>
          Changes apply to the next notification sent with this key, with no deploy. Placeholders such as{' '}
          <code>{'{{amountEn}}'}</code> are filled by the code that sends it, so both languages must use the same ones.
        </Alert>
        <TextField
          margin="normal"
          fullWidth
          required
          id="notification-template-key"
          name="key"
          label="Key"
          value={values.key}
          onChange={handleChange}
          disabled={!canUpdate}
          inputProps={{ maxLength: KEY_MAX_LENGTH, dir: 'ltr' }}
          error={Boolean(keyError) || taken}
          helperText={
            taken ? 'Another push template already has this key.' : keyError || 'What the code sends this template by.'
          }
        />
        {keyChanged && !keyError && !taken && template && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {describeKeyRename(template.key, edited.key)}
          </Alert>
        )}
        <TemplateCopyFields values={values} onChange={handleChange} disabled={!canUpdate} />
        <FormControlLabel
          control={
            <Switch
              checked={values.isActive}
              onChange={(event) => setFieldValue('isActive', event.target.checked)}
              disabled={!canUpdate}
            />
          }
          label={values.isActive ? 'Active: notifications with this key are sent' : 'Inactive: nothing is sent'}
        />
        {problems.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {problems.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </Alert>
        )}
        {introduced.length > 0 && problems.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            New placeholder{introduced.length > 1 ? 's' : ''}: {introduced.map((name) => `{{${name}}}`).join(', ')}.
            Only use a placeholder the code that sends this key fills. If it doesn&apos;t, the notification is not sent
            at all.
          </Alert>
        )}
        {saveErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveErrors.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </Alert>
        )}
        {template && <NotificationTemplateHistory channel="PUSH" templateId={template.id} />}
      </DialogContent>
      <DialogActions>
        {canUpdate && (
          <LoadingButton
            type="button"
            color="primary"
            variant="contained"
            endIcon={<SaveIcon />}
            loading={isLoading}
            loadingPosition="end"
            disabled={!isDirty || Boolean(keyError) || taken || problems.length > 0}
            onClick={submitForm}
          >
            Save
          </LoadingButton>
        )}
        <Button color="inherit" variant="outlined" onClick={handleClose}>
          {canUpdate ? 'Cancel' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
